/* ═══════════════════════════════════════════════════════════════
 * pod-hmac.js
 * 落点: assets/pod-hmac.js
 * 作用: 前端 HMAC-SHA256 签名 helper.
 *
 * 与 Cloudflare Worker 端 hmacSha256Hex() (workers/podw-upload-worker.js:797)
 * 以及 hmacSha256HexInternal() (workers/pod-internal-api.js:1644)
 * 输出完全一致: hex string, 小写.
 *
 * 用法:
 *   1. 简单调用 (附到现有 fetch 上):
 *      const sig = await window.POD_HMAC.signHex(secret, message);
 *      fetch(url, { headers: { 'X-Signature': sig, ... } });
 *
 *   2. 完整包装 (返回带签名的 fetch options):
 *      const opts = await window.POD_HMAC.signedFetchOptions(secret, body, 'X-Signature');
 *      fetch(url, opts);
 *
 *   3. 把响应结果解析为 JSON:
 *      const result = await window.POD_HMAC.signedFetchJSON(url, body, secret, headerName);
 *
 * 与 worker 端算法对齐 (关键):
 *   - TextEncoder().encode(secret) + TextEncoder().encode(message)
 *   - crypto.subtle.importKey('raw', ..., { name: 'HMAC', hash: 'SHA-256' })
 *   - crypto.subtle.sign('HMAC', key, ...)
 *   - toString(16).padStart(2, '0') → 小写 hex
 *
 * 浏览器兼容性:
 *   - crypto.subtle 在所有现代浏览器可用 (Chrome 66+, Firefox 55+, Safari 10.1+)
 *   - 仅 HTTPS / localhost 有效 (浏览器安全策略)
 * ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /**
   * 核心: 计算 HMAC-SHA256 hex (小写).
   * 与 Cloudflare Worker 端的 hmacSha256Hex() 一致.
   *
   * @param {string} secret - 共享密钥
   * @param {string} message - 待签名消息
   * @returns {Promise<string>} hex string, 64 chars, lowercase
   */
  async function signHex(secret, message) {
    if (!secret || typeof secret !== 'string') {
      // 没配置 secret 时返回空签名 — Worker 端若没配 POD_INTERNAL_API_SECRET 也会放行.
      // 调用方应在调用前 console.warn 提示用户配置 secret.
      return '';
    }
    if (typeof message !== 'string') {
      throw new Error('[POD_HMAC] signHex: message must be string');
    }
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('[POD_HMAC] crypto.subtle unavailable — requires HTTPS or localhost');
    }
    const encoder = new TextEncoder();
    const key = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const sig = await window.crypto.subtle.sign('HMAC', key, encoder.encode(message));
    return Array.from(new Uint8Array(sig))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  /**
   * 包装: 为 fetch URL+body 生成签名 + 时间戳.
   * 签名 payload = "method|path|body|timestamp" (与 Worker 端保持一致,
   * 若 Worker 端用别的 payload, 需要对齐).
   *
   * @param {string} secret
   * @param {string} payload - 与 Worker 端 verify 时的 payload 拼接规则一致
   * @param {string} headerName - 默认 'X-Signature'
   * @returns {Promise<{signature: string, timestamp: string, headers: object}>}
   */
  async function buildSignedHeaders(secret, payload, headerName) {
    const ts = Math.floor(Date.now() / 1000).toString();
    // Worker 端 verify 顺序: `${orderId}:${ts}` (internal-api) 或 `${sessionId}:${orderId}:${date}` (upload-worker)
    // 调用方需传入正确的 payload (例如 internal-api 用 `${orderId}:${ts}`, upload-worker 用 `${sessionId}:${orderId}:${date}`)
    // 这里只生成纯 HMAC, 不强制 payload 结构.
    const signature = await signHex(secret, payload);
    return {
      signature: signature,
      timestamp: ts,
      headers: {
        'X-Signature': signature
      }
    };
  }

  /**
   * 包装: 给 fetch options 加签名头.
   * 适用于 POST JSON 调用, payload 拼接规则:
   *   - upload-worker /rename: `${sessionId}:${orderId}:${date}`
   *   - upload-worker /upload: `${sessionId}:${contentType}` (worker 端有变化时需对齐)
   *
   * @param {string} url
   * @param {object} body - JS 对象
   * @param {string} payload - Worker 端 verify 时拼接的字符串 (调用方负责)
   * @param {object} [extraHeaders] - 额外 headers (例如 X-Pod-Signature 用 'internal' 模式)
   * @param {string} [headerName='X-Signature']
   * @returns {Promise<object>} fetch options with method=POST, headers, body=JSON.stringify
   */
  async function signedFetchOptions(url, body, payload, extraHeaders, headerName) {
    const secret = (extraHeaders && extraHeaders.hmacSecret) || window.POD_HMAC_DEFAULT_SECRET || '';
    const sig = await signHex(secret, payload);
    const headers = {
      'Content-Type': 'application/json'
    };
    if (extraHeaders) {
      Object.keys(extraHeaders).forEach(function (k) {
        if (k !== 'hmacSecret') headers[k] = extraHeaders[k];
      });
    }
    headers[headerName || 'X-Signature'] = sig;
    return {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body)
    };
  }

  /**
   * 便捷: 一步完成 fetch + JSON parse, 自动带签名.
   *
   * @param {string} url
   * @param {object} body
   * @param {string} secret
   * @param {string} payload - Worker 端 verify 时拼接的字符串
   * @param {string} [headerName='X-Signature']
   * @returns {Promise<object>} response JSON
   */
  async function signedFetchJSON(url, body, secret, payload, headerName) {
    const sig = await signHex(secret, payload);
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [headerName || 'X-Signature']: sig
      },
      body: JSON.stringify(body)
    });
    const text = await resp.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error('[POD_HMAC] signedFetchJSON: non-JSON response (status=' + resp.status + '): ' + text.slice(0, 200));
    }
  }

  /**
   * 便捷: 内部 API 签名 (X-Pod-Signature + X-Pod-Timestamp).
   * Worker 端 verify: hmac(`${orderId}:${ts}`), 见 pod-internal-api.js:1682.
   *
   * @param {string} url
   * @param {object} body - 必须包含 orderId (Worker 端 verify 需要)
   * @param {string} secret
   * @returns {Promise<object>} response JSON
   */
  async function signedInternalFetchJSON(url, body, secret) {
    const ts = Math.floor(Date.now() / 1000).toString();
    const orderId = (body && body.orderId) || '';
    const sig = await signHex(secret, orderId + ':' + ts);
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Pod-Signature': sig,
        'X-Pod-Timestamp': ts
      },
      body: JSON.stringify(body)
    });
    const text = await resp.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error('[POD_HMAC] signedInternalFetchJSON: non-JSON response (status=' + resp.status + '): ' + text.slice(0, 200));
    }
  }

  // 暴露到全局
  window.POD_HMAC = {
    signHex: signHex,
    buildSignedHeaders: buildSignedHeaders,
    signedFetchOptions: signedFetchOptions,
    signedFetchJSON: signedFetchJSON,
    signedInternalFetchJSON: signedInternalFetchJSON
  };
})();